import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { codigoDoErro } from '@/lib/apiEnvelope'
import { checkForAppUpdate } from '@/lib/appUpdate'
import { authStore } from '@/stores/authStore'
import type { ApiResponse } from '@/types/api'
import type { AuthSession } from '@/types/auth'

import { env } from './env'

const AUTH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/desbloqueio',
]

function isAuthPath(url: string | undefined): boolean {
  return !!url && AUTH_PATHS.some((path) => url.includes(path))
}

const commonConfig = {
  baseURL: env.apiBaseUrl,
  // Necessário para enviar/receber o cookie httpOnly do refresh token.
  withCredentials: true,
  // Uma API travada nunca pode travar a tela.
  timeout: 15_000,
}

export const httpClient = axios.create(commonConfig)

// Instância sem interceptors — usada apenas no refresh, para não recursar.
const bareClient = axios.create(commonConfig)

httpClient.interceptors.request.use((config) => {
  const token = authStore.getState().accessToken
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  return config
})

let lastKnownApiVersion: string | null = null

/**
 * A API manda `X-App-Version` em toda resposta (ver Program.cs). Se o valor
 * mudar enquanto a aba está aberta, é sinal de que saiu um deploy novo — e
 * como front e API sobem juntos, aproveita pra checar se também há uma
 * versão nova do PWA, sem esperar o polling de 60s do `useAppUpdater`.
 */
function watchApiVersion(version: unknown) {
  if (typeof version !== 'string') return

  if (lastKnownApiVersion && lastKnownApiVersion !== version) {
    checkForAppUpdate()
  }
  lastKnownApiVersion = version
}

httpClient.interceptors.response.use(
  (response) => {
    watchApiVersion(response.headers['x-app-version'])
    return response
  },
  (error: AxiosError) => {
    watchApiVersion(error.response?.headers['x-app-version'])
    return Promise.reject(error)
  },
)

export const BIOMETRIA_NECESSARIA = 'BIOMETRIA_NECESSARIA'

function isBiometriaNecessaria(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    codigoDoErro(error) === BIOMETRIA_NECESSARIA
  )
}

let refreshPromise: Promise<string | null> | null = null

/**
 * Renova a sessão a partir do cookie httpOnly `token_renovacao`.
 * Single-flight: requisições concorrentes compartilham a mesma promessa.
 */
export function refreshSession(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = bareClient
    .post<ApiResponse<AuthSession>>('/auth/refresh')
    .then((response) => {
      authStore.getState().setSession(response.data.data)
      return response.data.data.accessToken
    })
    .catch((error: unknown) => {
      // Sessão vinculada à biometria: trava a tela (desbloqueio) em vez de ir para o login.
      if (isBiometriaNecessaria(error)) authStore.getState().lock()
      else authStore.getState().clearSession()
      throw error
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined
    const status = error.response?.status

    if (status !== 401 || !config || config._retry || isAuthPath(config.url)) {
      return Promise.reject(error)
    }

    config._retry = true
    try {
      const token = await refreshSession()
      if (token) config.headers.set('Authorization', `Bearer ${token}`)
      return httpClient(config)
    } catch {
      // Refresh falhou — a sessão já foi limpa; propaga o erro original.
      return Promise.reject(error)
    }
  },
)
